import { SelectionSet } from './../../../pages/settings/settings.service';
import { SelectStatementResult } from 'src/app/models/select-statement-result.model';
import { BaseSelectStatementId } from 'src/app/enums/base-select-statement-id.enum';
import { SettingsService } from 'src/app/pages/settings/settings.service';
import { ServiceLocator } from './../../../tools/service-locator.tool';
import { CoreService } from './../../../services/core.service';
import { Subject, Observable, map, Subscription, tap, switchMap, of } from 'rxjs';
import { DropdowndSelectedValues } from './../../dropdown-filter/dropdown-filter.component';
import { OperationType } from './../miscellaneous/operation-type.enum';
import { OverviewFilterInput, OverviewFiltersOutput } from "src/app/pages/stock/stock.service";
import { QueryFilterParameter } from '../models/query-filter-parameter.model';
import { SelectionOptionsPopUpService } from 'src/app/selection-options-pop-up.service';
import { RequestOptions } from 'src/app/services/base.service';
import { SelectStatementOptions } from 'src/app/models/select-statement-options.model';
import { Filters } from '../types/filters.types';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { inject } from '@angular/core';
import { FiltersGroupsSettingsPopUpService } from '../services/filters-groups-settings-pop-up.service';
import { FiltersGroupSettings } from '../models/filters-group-settings.model';
import { CounterGroups } from 'src/app/services/loading-indication.service';

export class FiltersOperator {

	private _localStorageService: LocalStorageService = ServiceLocator.injector.get(LocalStorageService);

	private _id?: number;
	private _routeId?: number;
	private _instanceIdentifier?: number;

	public initialize(id: number, routeId: number, instanceIdentifier: number): void {
		this._id = id;
		this._routeId = routeId;
		this._instanceIdentifier = instanceIdentifier;

		const localStorageKey = `${Filters.LocalStorageKeys.FiltersGroupSettingsPrefix}-route(${this._routeId})_operator(${this._id})`;

		const cachedFiltersGroupSettings = this.filtersGroupSettings ?? this._localStorageService.get<FiltersGroupSettings>(localStorageKey);

		if (cachedFiltersGroupSettings) {

			this.filtersGroupSettings = cachedFiltersGroupSettings;
			return;
		}

		this._coreService.getFiltersGroupSettings(
			this._id,
			{
				name: 'Load Filters Group',
				showLoadingIndicator: true,

				loadingIndicatorCounterGroupType: CounterGroups.FILTERS_LOAD,
				loadingIndicatorCounterGroupId: this._id.toString()
			}
		).pipe(
			switchMap(
				(filtersGroupSettings: FiltersGroupSettings) => {
					if (filtersGroupSettings) {
						return of(filtersGroupSettings);
					}

					return this._coreService.createFiltersGroupSettings(
						this._id,
						{
							name: 'Create Filters Group',
							showLoadingIndicator: true,
						}
					)
				}
			)
		).subscribe(
			{
				next: (filtersGroupSettings: FiltersGroupSettings) => {
					this.filtersGroupSettings = filtersGroupSettings;

					this._localStorageService.set<FiltersGroupSettings>(localStorageKey, this.filtersGroupSettings);
				},
			}
		);
	}

	private _filtersGroupsSettingsPopUpService: FiltersGroupsSettingsPopUpService = inject(FiltersGroupsSettingsPopUpService);

	private _filtersGroupSettings: FiltersGroupSettings = null;
	public get filtersGroupSettings(): FiltersGroupSettings {
		return this._filtersGroupSettings;
	}
	public set filtersGroupSettings(value: FiltersGroupSettings) {
		this._filtersGroupSettings = value;

		this._filtersGroupSettings.routeId = this._routeId;

		this._filtersGroupsSettingsPopUpService.addFiltersGroupSettings(this._filtersGroupSettings);
	}

	public applyButtonEnabled: boolean = true;

	private _overviewFiltersInputs: OverviewFilterInput[] = [];
	public get overviewFiltersInputs(): OverviewFilterInput[] {
		return this._overviewFiltersInputs;
	}
	public set overviewFiltersInputs(value: OverviewFilterInput[]) {
		this._overviewFiltersInputs = value;

		this.applyButtonEnabled = false;

		for (let a = 0; a < this._overviewFiltersInputs.length; a++) {
			const overviewFiltersInput: OverviewFilterInput = this._overviewFiltersInputs[a];

			if (!overviewFiltersInput.instantSelectionApplication) {
				this.applyButtonEnabled = true;

				break;
			}
		}
	}

	public overviewFiltersOutput: OverviewFiltersOutput = new OverviewFiltersOutput();

	public get appliedFilters(): QueryFilterParameter[] {
		return this.overviewFiltersOutput.filters;
	}

	public onFiltersApplied: Subject<{ input: OverviewFilterInput[], output: OverviewFiltersOutput }> = new Subject<{ input: OverviewFilterInput[], output: OverviewFiltersOutput }>();

	public applyFilters() {
		let filters: QueryFilterParameter[] = [];

		if (this.overviewFiltersOutput.filters) {
			for (let a = 0; a < this.overviewFiltersInputs.length; a++) {
				const overviewFilterInput: OverviewFilterInput = this.overviewFiltersInputs[a];

				if (overviewFilterInput.selectedValues) {
					for (let b = 0; b < overviewFilterInput.selectedValues.length; b++) {
						let selectedValue: string = overviewFilterInput.selectedValues[b];

						selectedValue = encodeURIComponent(selectedValue);

						const filter: QueryFilterParameter = new QueryFilterParameter(
							overviewFilterInput.selectionName,
							selectedValue,
							OperationType.Equals
						);

						filters.push(filter);
					}
				}
			}
		}

		this.overviewFiltersOutput.filters = filters.map(
			(queryFilterParameter: QueryFilterParameter) => {
				queryFilterParameter.value = decodeURIComponent(queryFilterParameter.value);

				return queryFilterParameter;
			}
		);

		this.onFiltersApplied.next({ input: this.overviewFiltersInputs, output: this.overviewFiltersOutput });
	}

	public onFiltersReset: Subject<{ input: OverviewFilterInput[], output: OverviewFiltersOutput }> = new Subject<{ input: OverviewFilterInput[], output: OverviewFiltersOutput }>();

	public resetFilters(options?: { applyFilters: boolean }) {
		this.overviewFiltersOutput.filters = [];

		for (let a = 0; a < this.overviewFiltersInputs.length; a++) {
			this.overviewFiltersInputs[a].selectedValues = [];
		}

		this.onFiltersReset.next({ input: this.overviewFiltersInputs, output: this.overviewFiltersOutput });

		if (options ? options.applyFilters : this._options.shouldApplyFiltersOnResetFilters) {
			this.applyFilters();
		}
	}

	public selectedItemsChanged(evt: DropdowndSelectedValues, filter: OverviewFilterInput) {
		filter.selectedValues = evt.values;

		if (filter.instantSelectionApplication) {
			if (filter.selectionMode === 'single') {
				this.applyFilters();
			} else if (filter.selectionMode === 'multiple' && filter.selectedValues.length === 0) {
				this.applyFilters();
			}
		}
	}

	public onClosed(evt: any, filter: OverviewFilterInput): void {
		if (filter.instantSelectionApplication && filter.selectionMode === 'multiple' && filter.selectedValues.length !== 0) {
			this.applyFilters();
		}
	}

	public setTextBoxFilterOptions(selectionId: number, event: any) {
		if (event.value) {
			for (let a = 0; a < this.overviewFiltersInputs.length; a++) {
				if (this.overviewFiltersInputs[a].selectionId === selectionId) {
					this.overviewFiltersInputs[a].selectedValues = [event.value];
				}
			}
		}
	}

	public toURLQuery(): string {
		let query = '';

		if (this.overviewFiltersOutput.filters) {
			for (let i = 0; i < this.overviewFiltersOutput.filters.length; i++) {
				const queryFilterParameter: QueryFilterParameter = this.overviewFiltersOutput.filters[i];

				query += `&filters.filters[${i}].fieldName=${queryFilterParameter.fieldName}&filters.filters[${i}].value=${queryFilterParameter.value}&filters.filters[${i}].operation=${queryFilterParameter.operation}`;
			}
		}

		return `filters.action=${this.overviewFiltersOutput.action}${query}`;
	}

	public getObservable = (requestOptions?: RequestOptions, selectStatementOptions: SelectStatementOptions = this.defaultSelectStatementOptions): Promise<Observable<OverviewFilterInput[]>> => this._options.getObservable(selectStatementOptions, requestOptions);
	public load = (requestOptions?: RequestOptions, selectStatementOptions: SelectStatementOptions = this.defaultSelectStatementOptions): Promise<Subscription> => this._options.load(selectStatementOptions, requestOptions);

	//#region <Services>

	private _coreService: CoreService = ServiceLocator.injector.get(CoreService);
	private _settingsService: SettingsService = ServiceLocator.injector.get(SettingsService);
	private _selectionOptionsPopUpService: SelectionOptionsPopUpService = ServiceLocator.injector.get(SelectionOptionsPopUpService);

	//#endregion </Services>

	constructor(
		public defaultSelectStatementOptions?: SelectStatementOptions,
		private _defaultAction: string = "Filter",
		private _options: {
			shouldApplyFiltersOnResetFilters: boolean,
			getObservable?: (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => Promise<Observable<OverviewFilterInput[]>>
			load?: (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => Promise<Subscription>
		} = {
				shouldApplyFiltersOnResetFilters: true,
				getObservable: async (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => {
					const $this = this;

					if (requestOptions) {
						requestOptions.loadingIndicatorCounterGroupType = CounterGroups.FILTERS_LOAD;
						requestOptions.loadingIndicatorCounterGroupId =  this._id.toString();
					}

					const observable$ = $this._coreService.getFilters(
						selectStatementOptions,
						requestOptions
					);

					return (await observable$).pipe(
						tap(
							(_) => {
								// Add filters selection set to SelectionOptions pop up.
								$this._coreService.getSelectStatementData<SelectionSet>(
									new SelectStatementOptions(
										{
											baseSelectStatementId: BaseSelectStatementId.DEV_GetSelectionSets,
										}
									),
									(
										requestOptions
											? Object.assign(
												{ ...requestOptions },
												{
													name: `FiltersOperator -> ${requestOptions?.name} -> BaseSelectStatementId.GetSelectionSets`
												}
											)
											: requestOptions
									),
									false
								).then(
									(promiseResponse: Observable<SelectStatementResult<SelectionSet>>) => {
										promiseResponse.subscribe(
											{
												next: async (selectStatementResult: SelectStatementResult<SelectionSet>) => {
													const selectionSetId: number = await selectStatementOptions.selectionSetId;

													const selectionSet: SelectionSet = selectStatementResult.data.find((selectionSet) => selectionSet.selectionSetId === selectionSetId);

													if (selectionSet) {
														$this._selectionOptionsPopUpService.addSelectionSet(selectionSet);
													}
												}
											}
										);
									}
								);
							}
						),
						map(
							(response: OverviewFilterInput[]) => {
								// Assign filters to operator.
								response.forEach(
									(filter: OverviewFilterInput) => {
										filter.values = filter.values ? filter.values : [];

										$this.overviewFiltersOutput.filters = $this.overviewFiltersOutput.filters ? $this.overviewFiltersOutput.filters : [];

										filter.values = filter.values.map(
											(value: any) => ({ all: value })
										);

										filter.selectedValues = $this.overviewFiltersOutput.filters.filter(
											(filterItem: QueryFilterParameter) => filterItem.fieldName === filter.selectionName
										).map(
											(filterItem: QueryFilterParameter) => filterItem.value
										);

										if (filter.selectedValues.length === 0 && !(filter.defaultFilterValue == null || filter.defaultFilterValue === "")) {
											filter.selectedValues.push(filter.defaultFilterValue);
										}
									}
								);

								$this.overviewFiltersInputs = response;

								return response;
							}
						),
					);
				},
				load: async (selectStatementOptions: SelectStatementOptions, requestOptions?: RequestOptions) => (await this._options.getObservable(selectStatementOptions, requestOptions)).subscribe()
			}
	) {
		this.overviewFiltersOutput.action = this._defaultAction;
	}
}
